import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import BackToTop from "../components/BackToTop";
import { useSEO } from "../lib/useSEO";
import { LEGAL, SITE } from "../lib/site";

export default function PolitiqueConfidentialite() {
  useSEO({
    title: "Politique de confidentialité — K'REUSE EXTÉRIEUR",
    description:
      "Politique de confidentialité et de protection des données personnelles de K'REUSE EXTÉRIEUR (RGPD).",
    canonical: "https://kreuse-exterieur.fr/politique-confidentialite",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://kreuse-exterieur.fr/" },
        { "@type": "ListItem", position: 2, name: "Politique de confidentialité", item: "https://kreuse-exterieur.fr/politique-confidentialite" },
      ],
    },
  });

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />
      <main className="pt-32 pb-24 max-w-3xl mx-auto px-6 md:px-10">
        <div className="text-sm tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-3">
          Vie privée
        </div>
        <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-light tracking-tight text-[#1A2E1C] mb-10">
          Politique de{" "}
          <span className="font-medium text-[#1B5E20]">confidentialité</span>
        </h1>

        <div className="space-y-8 text-[#1A2E1C] leading-relaxed">
          <p className="text-[#5C705E]">
            Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long" })}
          </p>

          <section>
            <h2 className="font-['Outfit'] text-2xl font-semibold mb-3">1. Responsable du traitement</h2>
            <p>
              Le responsable du traitement des données est <strong>K'REUSE EXTÉRIEUR ({LEGAL.status})</strong>,
              représentée par {LEGAL.director}, dont le siège social est situé au {LEGAL.address}.
              SIRET : {LEGAL.siret}.
            </p>
          </section>

          <section>
            <h2 className="font-['Outfit'] text-2xl font-semibold mb-3">2. Données collectées</h2>
            <p>Lorsque vous utilisez nos formulaires (contact, demande de location), nous collectons :</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Nom et prénom</li>
              <li>Adresse email</li>
              <li>Numéro de téléphone</li>
              <li>Service demandé / matériel souhaité</li>
              <li>Date souhaitée (pour la location)</li>
              <li>Contenu de votre message</li>
            </ul>
          </section>

          <section>
            <h2 className="font-['Outfit'] text-2xl font-semibold mb-3">3. Finalité du traitement</h2>
            <p>Vos données sont utilisées uniquement pour :</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Répondre à votre demande de devis ou de contact</li>
              <li>Confirmer une réservation de matériel</li>
              <li>Assurer le suivi de la prestation</li>
            </ul>
            <p className="mt-3">
              <strong>Aucune donnée n'est cédée, vendue ou utilisée à des fins marketing.</strong>
            </p>
          </section>

          <section>
            <h2 className="font-['Outfit'] text-2xl font-semibold mb-3">4. Durée de conservation</h2>
            <p>
              Vos données sont conservées pendant la durée nécessaire au traitement de votre demande
              et au suivi commercial, et au maximum <strong>3 ans</strong> à compter de notre dernier
              contact, conformément aux recommandations de la CNIL.
            </p>
          </section>

          <section>
            <h2 className="font-['Outfit'] text-2xl font-semibold mb-3">5. Base légale</h2>
            <p>
              Le traitement est fondé sur votre consentement explicite (envoi volontaire d'un formulaire)
              et sur l'exécution de mesures précontractuelles (établissement de devis).
            </p>
          </section>

          <section>
            <h2 className="font-['Outfit'] text-2xl font-semibold mb-3">6. Vos droits (RGPD)</h2>
            <p>Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Droit d'accès à vos données</li>
              <li>Droit de rectification</li>
              <li>Droit à l'effacement (« droit à l'oubli »)</li>
              <li>Droit à la limitation du traitement</li>
              <li>Droit à la portabilité</li>
              <li>Droit d'opposition</li>
            </ul>
            <p className="mt-3">
              Pour exercer ces droits, contactez-nous par email à{" "}
              <a href={`mailto:${SITE.email}`} className="text-[#1B5E20] hover:underline font-medium">
                {SITE.email}
              </a>
              {" "}ou par téléphone au{" "}
              <a href={`tel:${SITE.phoneRaw}`} className="text-[#1B5E20] hover:underline font-medium">
                {SITE.phone}
              </a>
              . Vous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).
            </p>
          </section>

          <section>
            <h2 className="font-['Outfit'] text-2xl font-semibold mb-3">7. Cookies & mesure d'audience</h2>
            <p>
              Le site <strong>kreuse-exterieur.fr</strong> utilise :
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong>Cookies techniques strictement nécessaires</strong> (mémorisation de votre choix de consentement, session administrateur).</li>
              <li>
                <strong>Google Analytics 4</strong> (G-73812K557N) — mesure d'audience anonymisée, déclenchée
                <strong> uniquement après votre consentement explicite</strong> via le bandeau cookies.
                L'IP est anonymisée. Aucune donnée n'est utilisée à des fins publicitaires.
              </li>
            </ul>
            <p className="mt-3">
              Vous pouvez à tout moment retirer votre consentement en effaçant le stockage local
              du site dans votre navigateur, ou en cliquant sur « Refuser » dans le bandeau qui
              s'affichera à nouveau.
            </p>
          </section>

          <section>
            <h2 className="font-['Outfit'] text-2xl font-semibold mb-3">8. Sécurité</h2>
            <p>
              Nous mettons en œuvre toutes les mesures techniques et organisationnelles appropriées
              pour protéger vos données contre tout accès non autorisé, perte, altération ou divulgation.
            </p>
          </section>

          <section>
            <h2 className="font-['Outfit'] text-2xl font-semibold mb-3">9. Modification</h2>
            <p>
              Cette politique peut être amenée à évoluer. La date de dernière mise à jour figure en
              haut du document.
            </p>
          </section>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTop />
    </div>
  );
}
